<template>
  <div>
    <section class="content-header">
      <div class="container-fluid">
        <div class="row mb-2">
          <div class="col-sm-6"></div>
        </div>
      </div>
    </section>
    <section class="content">
      <div class="container-fluid">
        <div class="card card-success">
          <div class="card-header">
            <h3 class="card-title">Membership Subscription History</h3>
          </div>
          <div class="loading" v-if="isLoading">
            <hour-glass></hour-glass>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-3">
                <input
                  v-model.trim="filters.search"
                  type="text"
                  class="form-control"
                  placeholder="Search reference, program, consumer, amount"
                  @keyup.enter="applyFilters"
                />
              </div>
              <div class="col-md-3">
                <select v-model="filters.status_code" class="form-control">
                  <option value="">All Status</option>
                  <option value="204">Active</option>
                  <option value="205">Failed</option>
                  <option value="1203">Consumer Declined</option>
                </select>
              </div>
              <div class="col-md-3">
                <input
                  class="form-control"
                  placeholder="Start Date"
                  id="subscription-start-date"
                  onkeydown="return false"
                  autocomplete="off"
                />
              </div>
              <div class="col-md-3">
                <input
                  class="form-control"
                  placeholder="End Date"
                  id="subscription-end-date"
                  onkeydown="return false"
                  autocomplete="off"
                />
              </div>
            </div>

          </div>
          <div class="card-footer">
            <button class="btn btn-success mr-2" @click="applyFilters">Generate</button>
            <button class="btn btn-secondary" @click="resetFilters">Clear</button>
          </div>
          <div class="card-body">
            <div class="table-responsive">
              <table class="table" style="width: 100%; white-space: normal">
                <thead>
                  <tr>
                    <th>Reference</th>
                    <th>Program</th>
                    <th>Consumer</th>
                    <th>Period</th>
                    <th>Status</th>
                    <th>Amount</th>
                    <th>Use Points</th>
                    <th>Next Billing</th>
                    <th>Last Billed</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in subscriptions" :key="item.id">
                    <td>{{ item.subscription_reference }}</td>
                    <td>{{ item.membership_program_title }}</td>
                    <td>{{ item.consumer_name || item.consumer_id }}</td>
                    <td>{{ item.membership_period }}</td>
                    <td>
                      <span class="status-badge" :class="statusClass(item)">{{ getSubscriptionStatusLabel(item) }}</span>
                    </td>
                    <td>${{ parseFloat(item.amount || 0).toFixed(2) }}</td>
                    <td>{{ Number(item.use_available_points) === 1 ? 'Yes' : 'No' }}</td>
                    <td>{{ formatDate(item.next_billing_date) }}</td>
                    <td>{{ formatDate(item.last_billed_at) }}</td>
                    <td>
                      <button class="btn btn-primary btn-sm" @click="viewHistory(item)">View</button>
                    </td>
                  </tr>
                  <tr v-if="!isLoading && subscriptions.length === 0">
                    <td colspan="10" class="text-center">No records found</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="subscription-pagination" v-if="pagination.total > 0">
              <div class="subscription-pagination-text">
                Showing {{ pageStart }} to {{ pageEnd }} of {{ pagination.total }} entries
              </div>
              <div class="btn-group btn-group-sm" role="group" aria-label="Subscription pagination">
                <button class="btn btn-outline-secondary" :disabled="pagination.page <= 1 || isLoading" @click="changePage(pagination.page - 1)">Previous</button>
                <button class="btn btn-outline-secondary active page-indicator" disabled>Page {{ pagination.page }}</button>
                <button class="btn btn-outline-secondary" :disabled="!pagination.has_more || isLoading" @click="changePage(pagination.page + 1)">Next</button>
              </div>
            </div>
          </div>
        </div>

        <b-modal
          id="subscription-history-modal"
          ref="subscription-history-modal"
          cancel-title="Close"
          cancel-variant="outline-secondary"
          :title="selectedSubscription ? 'Billing Attempts - ' + selectedSubscription.subscription_reference : 'Billing Attempts'"
          hide-footer
          :header-text-variant="headerTextVariant"
          :no-close-on-esc="true"
          :no-close-on-backdrop="true"
          @close="hideModal"
          @cancel="hideModal"
          @hidden="closeModal"
        >
          <div class="table-responsive">
            <table class="table table-hover" style="width: 100%; white-space: normal">
                  <thead>
                    <tr>
                      <th>Cycle</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Points Used</th>
                      <th>Bank Charged</th>
                      <th>Attempted At</th>
                      <th>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in history" :key="row.id">
                      <td>{{ row.billing_cycle_number }}</td>
                      <td>{{ row.billing_type }}</td>
                      <td>
                        <span class="status-badge" :class="historyStatusClass(row)">{{ getHistoryStatusLabel(row) }}</span>
                      </td>
                      <td>${{ parseFloat(row.points_applied_amount || 0).toFixed(2) }}</td>
                      <td>${{ parseFloat(row.bank_charged_amount || 0).toFixed(2) }}</td>
                      <td>{{ formatDate(row.attempted_at) }}</td>
                      <td>{{ row.response_message || '-' }}</td>
                    </tr>
                    <tr v-if="history.length === 0">
                      <td colspan="7" class="text-center">No history found</td>
                    </tr>
                  </tbody>
                </table>
          </div>
        </b-modal>
      </div>
    </section>
  </div>
</template>

<script>
import subscriptionApi from "../../api/subscriptions";
import moment from "moment";
import { HourGlass } from "vue-loading-spinner";

export default {
  name: "SubscriptionHistory",
  components: { HourGlass },
  computed: {
    pageStart() {
      if (!this.pagination.total) {
        return 0;
      }
      return (this.pagination.page - 1) * this.pagination.per_page + 1;
    },
    pageEnd() {
      return Math.min(this.pagination.page * this.pagination.per_page, this.pagination.total);
    },
  },
  data() {
    return {
      
      headerTextVariant:"light",
      isLoading: false,
      isHistoryLoading: false,
      subscriptions: [],
      selectedSubscription: null,
      history: [],
      pagination: {
        page: 1,
        per_page: 10,
        total: 0,
        has_more: false,
      },
      filters: {
        search: "",
        status_code: "",
        from_date: "",
        to_date: "",
      },
    };
  },
  methods: {
    loadData(page = 1) {
      this.isLoading = true;
      const request = {
        page,
        per_page: this.pagination.per_page,
      };

      if (this.filters.search) {
        request.search = this.filters.search;
      }
      if (this.filters.status_code) {
        request.status_code = this.filters.status_code;
      }
      if (this.filters.from_date) {
        request.from_date = this.filters.from_date;
      }
      if (this.filters.to_date) {
        request.to_date = this.filters.to_date;
      }

      subscriptionApi
        .listSubscriptions(request)
        .then((response) => {
          const payload = response.data || {};
          this.subscriptions = payload.data || [];
          this.pagination = payload.pagination || {
            page: 1,
            per_page: 10,
            total: this.subscriptions.length,
            has_more: false,
          };
        })
        .catch(() => {
          this.subscriptions = [];
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    applyFilters() {
      this.loadData(1);
    },
    resetFilters() {
      this.filters.search = "";
      this.filters.status_code = "";
      this.filters.from_date = "";
      this.filters.to_date = "";
      $("#subscription-start-date").datepicker("clearDates");
      $("#subscription-end-date").datepicker("clearDates");
      this.loadData(1);
    },
    changePage(page) {
      if (page < 1 || this.isLoading) {
        return;
      }
      this.loadData(page);
    },
    viewHistory(item) {
      this.selectedSubscription = item;
      this.history = [];
      this.loading = true;
      subscriptionApi
        .subscriptionHistory({ subscription_reference: item.subscription_reference })
        .then((response) => {
          this.history = response.data ? response.data.history : [];
          this.$refs["subscription-history-modal"].show();
        })
        .catch(() => {
          this.history = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    hideModal() {
      this.$refs["subscription-history-modal"].hide();
    },
    closeModal() {
      this.selectedSubscription = null;
      this.history = [];
      this.isHistoryLoading = false;
    },
    formatDate(value) {
      return value ? moment(value).format("MM/DD/YYYY hh:mm A") : "-";
    },
    getSubscriptionStatusLabel(item) {
      const statusCode = Number(item.status_code);
      const reason = String(item.failed_reason || "").toLowerCase();
      const statusText = String(item.status || "").toLowerCase();

      if (statusCode === 204 || statusText === "active") {
        return "Active";
      }
      if (reason.includes("consumer declined") || reason.includes("declined by consumer") || reason.includes("cancelled by consumer") || reason.includes("canceled by consumer")) {
        return "Consumer Declined";
      }
      if (statusCode === 1203) {
        return "Consumer Declined";
      }
      return "Failed";
    },
    getHistoryStatusLabel(row) {
      const statusCode = Number(row.status_code);
      const statusText = String(row.status || "").toLowerCase();

      if (statusCode === 1 || statusText === "success") {
        return "Success";
      }
      return "Failed";
    },
    statusClass(item) {
      const label = this.getSubscriptionStatusLabel(item);
      if (label === "Active") {
        return "status-badge-success";
      }
      if (label === "Consumer Declined") {
        return "status-badge-danger";
      }
      return "status-badge-danger-light";
    },
    historyStatusClass(row) {
      return this.getHistoryStatusLabel(row) === "Success" ? "status-badge-success" : "status-badge-danger-light";
    },
  },
  mounted() {
    const self = this;
    $("#subscription-start-date").datepicker({
      format: "mm/dd/yyyy",
      autoclose: true,
      todayHighlight: true,
      startDate: new Date("2021-07-01"),
    });
    $("#subscription-end-date").datepicker({
      format: "mm/dd/yyyy",
      autoclose: true,
      todayHighlight: true,
    });
    $("#subscription-start-date, #subscription-end-date").on("change", function() {
      self.filters.from_date = $("#subscription-start-date").val() ? moment($("#subscription-start-date").val()).format("YYYY-MM-DD") : "";
      self.filters.to_date = $("#subscription-end-date").val() ? moment($("#subscription-end-date").val()).format("YYYY-MM-DD") : "";
    });
    this.loadData();
  },
  beforeDestroy() {
    $("#subscription-start-date, #subscription-end-date").off("change");
  },
};
</script>

<style scoped>
.status-badge {
  display: inline-block;
  min-width: 120px;
  text-align: center;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
}

.status-badge-success {
  background: #e7f6ea;
  color: #1f7a35;
}

.status-badge-danger {
  background: #fdecec;
  color: #b42318;
}

.status-badge-danger-light {
  background: #fff3f2;
  color: #c2410c;
}
.loading {
  text-align: center;
}

.subscription-pagination {
  margin-top: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.subscription-pagination-text {
  color: #6c757d;
  font-size: 14px;
}

.page-indicator {
  pointer-events: none;
  background: #e9ecef;
  color: #495057;
  border-color: #ced4da;
}
</style>
